/**
 * Dedicated Axios instance for internal API calls.
 *
 * This prevents the Bearer token from being leaked to third-party
 * domains when the global axios instance is used for external requests.
 *
 * Usage:  import apiClient from '../Config/apiClient';
 *         apiClient.get('/api/...');
 */
import axios from 'axios';
import { clearSessionAndRedirect } from '../Utils/logoutRedirect';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Mutex to prevent concurrent refresh calls (BLACKLIST_AFTER_ROTATION=True)
let axiosRefreshPromise = null;

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach Bearer token only to internal API calls
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — handle 401 with automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Reuse in-flight refresh to avoid blacklisting race conditions
      if (!axiosRefreshPromise) {
        axiosRefreshPromise = (async () => {
          try {
            const refreshToken = sessionStorage.getItem('refresh_token');
            if (!refreshToken) throw new Error('No refresh token');

            const response = await axios.post(`${API_BASE}/api/token/refresh/`, {
              refresh: refreshToken,
            });

            if (response.data.access) {
              sessionStorage.setItem('access_token', response.data.access);
              if (response.data.refresh) {
                sessionStorage.setItem('refresh_token', response.data.refresh);
              }
              return response.data.access;
            }
            throw new Error('No access token in response');
          } catch (err) {
            clearSessionAndRedirect();
            throw err;
          } finally {
            axiosRefreshPromise = null;
          }
        })();
      }

      try {
        const newAccessToken = await axiosRefreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
